import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import { Card } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney } from '../finance/money';

type Summary = {
  personId: string; name: string; self: boolean;
  membership: { status: string; since: string | null } | null;
  family: number;
  ministries: { id: string; name: string }[];
  cells: { id: string; name: string }[];
  attendance: { count: number; last: string | null } | null;
  classrooms: { id: string; name: string; role: 'maestro' | 'alumno' }[];
  reading: { plans: { planId: string; title: string }[]; groups: { id: string; name: string }[] };
  camps: { id: string; name: string; startsOn: string; status: string }[];
  baptism: { status: string; baptizedOn: string | null } | null;
  walletCents: number | null;
  contributions: { totalCents: number; count: number; since: string } | null;
  openCounseling: number | null;
};

const MEMBERSHIP: Record<string, string> = { active: 'Miembro activo', pending: 'Solicitud pendiente', ended: 'Membresía terminada', rejected: 'Solicitud rechazada' };
const BAPTISM: Record<string, string> = { solicitud: 'Pidió bautizarse', entrevista: 'En entrevista', preparacion: 'En preparación', autorizado: 'Autorizado', programado: 'Bautismo programado', bautizado: 'Bautizado', cancelado: 'Proceso cancelado' };
const day = (iso: string | null) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/** Perfil 360°: la vida de la persona en la iglesia de un vistazo (la persona y Pastoral). */
export function Profile360Card({ churchId, personId }: { churchId: string; personId: string }) {
  const data = useAsync((signal) => api.get<Summary>(`/reporting/churches/${churchId}/people/${personId}/360`, signal), [churchId, personId]);
  if (data.status !== 'ready') return null;
  const s = data.data;
  const rows: [string, React.ReactNode][] = [
    ['Membresía', s.membership ? `${MEMBERSHIP[s.membership.status] ?? s.membership.status}${s.membership.since ? ` desde ${day(s.membership.since)}` : ''}` : 'Sin membresía'],
    ['Familia', s.family ? `${s.family} ${s.family === 1 ? 'familiar' : 'familiares'} en la iglesia` : 'Sin familia registrada'],
    ['Ministerios', s.ministries.length ? s.ministries.map((m) => m.name).join(', ') : 'Ninguno'],
    ['Células', s.cells.length ? s.cells.map((c) => c.name).join(', ') : 'Ninguna'],
    ['Asistencia', s.attendance ? `${s.attendance.count} cultos registrados${s.attendance.last ? ` · último ${day(s.attendance.last)}` : ''}` : '—'],
    ['Formación', s.classrooms.length ? s.classrooms.map((c) => `${c.name}${c.role === 'maestro' ? ' (maestro)' : ''}`).join(', ') : 'Sin salones'],
    ['Bautismo', s.baptism ? `${BAPTISM[s.baptism.status] ?? s.baptism.status}${s.baptism.baptizedOn ? ` · ${day(s.baptism.baptizedOn)}` : ''}` : 'Sin proceso'],
    ['Lectura', [...s.reading.plans.map((p) => p.title), ...s.reading.groups.map((g) => `${g.name} (en grupo)`)].join(', ') || 'Ningún plan activo'],
    ['Campamentos', s.camps.length ? s.camps.map((c) => c.name).join(', ') : 'Ninguno'],
  ];
  if (s.walletCents !== null) rows.push(['Monedero', formatMoney(s.walletCents, 'MXN')]);
  if (s.contributions) rows.push(['Aportaciones este año', `${formatMoney(s.contributions.totalCents, 'MXN')} (${s.contributions.count})`]);
  if (s.openCounseling !== null) rows.push(['Consejería', s.openCounseling ? <Link to="/app/consejeria?ver=solicitudes">{s.openCounseling} abierta(s)</Link> : 'Nada abierto']);
  return (
    <Card title={s.self ? 'Mi vida en la iglesia (360°)' : 'Perfil 360°'}>
      <dl className="details">
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {s.self ? <p className="small muted">Solo tú y Pastoral ven este resumen.</p> : null}
    </Card>
  );
}
